/**
 * CMYK 色板业务层 — 对接 Apifox scj CMYK色板 Controller（文档 12）
 */

import { paletteService } from '@/services/artApi';
import {
  unwrapApiList,
  unwrapApiResult,
  mapPaletteColorDtoToPlatform,
  mapPlatformColorToSavePayload,
} from '@/utils/artMapper';
import {
  buildDepartmentOptions,
  getDepartmentRequestParams,
  getMaterialDepartmentContext,
  getMaterialDepartmentIdParam,
  isAllPlatformStore,
  resolvePlatformStorageKey,
  toPlatformStoreId,
  resolveShopId,
} from '@/utils/materialApiContext';

const GLOBAL_SHOP_KEY = import.meta.env.VITE_GLOBAL_SHOP_ID || '0';

function resolvePaletteDepartmentStoreKey(storeKey) {
  if (storeKey === GLOBAL_SHOP_KEY || storeKey === 's0' || storeKey === '0') return 'all';
  return storeKey;
}

function departmentParams(storeKey, user, apiContext = {}) {
  // 组织树选中「全平台」时，写操作一律不带 departmentId / departmentName
  if (isAllPlatformStore(apiContext.storeId)) return {};
  const sid = resolvePaletteDepartmentStoreKey(storeKey);
  const { stores, sessionDept } = buildDepartmentOptions({ storeId: sid, user, ...apiContext });
  return getDepartmentRequestParams(sid, user, stores, sessionDept);
}

function departmentGetParams(storeKey, user, apiContext = {}) {
  if (isAllPlatformStore(apiContext.storeId)) return {};
  const sid = resolvePaletteDepartmentStoreKey(storeKey);
  const { stores, sessionDept } = buildDepartmentOptions({ storeId: sid, user, ...apiContext });
  return getMaterialDepartmentIdParam(sid, user, stores, sessionDept);
}

/** 以色卡接口可用的确定性 paletteId：店铺用 shopId，全平台用全局 shopId（默认 0） */
export function resolvePaletteId(storeKey, user) {
  if (storeKey === GLOBAL_SHOP_KEY || storeKey === '0' || storeKey === 's0') {
    return Number(GLOBAL_SHOP_KEY) || 0;
  }
  return Number(resolveShopId(storeKey, user)) || 1;
}

function ensureCommonColorsMeta(platform) {
  if (!platform.commonColors) {
    platform.commonColors = { global: [], stores: {}, _meta: { globalPaletteId: null, storePaletteIds: {} } };
  }
  if (!platform.commonColors._meta) {
    platform.commonColors._meta = { globalPaletteId: null, storePaletteIds: {} };
  }
  if (!platform.commonColors.stores) platform.commonColors.stores = {};
  if (!Array.isArray(platform.commonColors.global)) platform.commonColors.global = [];
}

function isRemoteColorId(id) {
  if (id == null || id === '') return false;
  const s = String(id);
  if (s.startsWith('c_')) return false;
  return Number.isFinite(Number(s));
}

async function tryResolveGroupPaletteId(storeKey, user, preferredId, apiContext = {}) {
  try {
    const payload = await paletteService.list(departmentGetParams(storeKey, user, apiContext));
    const palettes = unwrapApiList(payload);
    if (!palettes.length) return null;
    if (preferredId != null) {
      const hit = palettes.find(p => Number(p.id) === Number(preferredId));
      if (hit?.id != null) return Number(hit.id);
    }
    return palettes[0]?.id != null ? Number(palettes[0].id) : null;
  } catch {
    return null;
  }
}

async function fetchColorsByPaletteId(paletteId) {
  const colorPayload = await paletteService.listColors({ paletteId: Number(paletteId) });
  return unwrapApiList(colorPayload).map(color => mapPaletteColorDtoToPlatform(color, paletteId));
}

export async function fetchPaletteScope(platform, storeKey, user, scope = 'store', apiContext = {}) {
  ensureCommonColorsMeta(platform);
  const fallbackId = resolvePaletteId(storeKey, user);
  const groupId = await tryResolveGroupPaletteId(storeKey, user, fallbackId, apiContext);
  const paletteId = groupId != null ? groupId : fallbackId;
  const colors = await fetchColorsByPaletteId(paletteId);

  if (scope === 'global') {
    platform.commonColors.global = colors;
    platform.commonColors._meta.globalPaletteId = String(paletteId);
  } else {
    const sid = toPlatformStoreId(resolveShopId(storeKey, user));
    platform.commonColors.stores[sid] = colors;
    platform.commonColors._meta.storePaletteIds[sid] = String(paletteId);
  }
  return { colors, defaultPaletteId: String(paletteId), palettes: [] };
}

export async function syncPaletteLibrary(platform, storeId, user, apiContext = {}) {
  const targetSid = isAllPlatformStore(storeId) ? 'all' : storeId;
  const results = await Promise.allSettled([
    fetchPaletteScope(platform, GLOBAL_SHOP_KEY, user, 'global', apiContext),
    fetchPaletteScope(platform, targetSid, user, 'store', apiContext),
  ]);
  const rejected = results.find(r => r.status === 'rejected');
  if (rejected && results.every(r => r.status === 'rejected')) {
    throw rejected.reason;
  }
}

export async function ensureDefaultPaletteGroup(storeKey, user, scope = 'store', stores = [], apiContext = {}) {
  const fallbackId = resolvePaletteId(storeKey, user);
  const existingId = await tryResolveGroupPaletteId(storeKey, user, fallbackId, { stores, ...apiContext });

  if (existingId != null) return { id: existingId };

  const dept = departmentParams(storeKey, user, { stores, ...apiContext });
  try {
    const payload = await paletteService.create({
      paletteId: fallbackId,
      paletteName: scope === 'global' ? '全平台色板' : '店铺色板',
      shopType: scope === 'global' ? 0 : 1,
      ...(dept.departmentId != null ? { departmentId: dept.departmentId } : {}),
      ...(dept.departmentName ? { departmentName: dept.departmentName } : {}),
    }, dept);
    const created = unwrapApiResult(payload);
    if (created?.id != null) return created;
  } catch {
    // 分组创建失败时仍可用派生 paletteId 读写色卡
  }
  return { id: fallbackId };
}

export async function savePaletteColorRemote(storeKey, user, scope, color, paletteId, stores = [], apiContext = {}) {
  let groupId = paletteId != null && paletteId !== '' ? Number(paletteId) : null;
  if (groupId == null || Number.isNaN(groupId)) {
    const palette = await ensureDefaultPaletteGroup(storeKey, user, scope, stores, apiContext);
    groupId = Number(palette?.id);
  }

  const dept = departmentParams(storeKey, user, { stores, ...apiContext });
  const editingId = isRemoteColorId(color?.id) ? Number(color.id) : null;
  const payload = await paletteService.addColor({
    ...mapPlatformColorToSavePayload({ ...color, id: undefined }, groupId),
    ...(dept.departmentId != null ? { departmentId: dept.departmentId } : {}),
    ...(dept.departmentName ? { departmentName: dept.departmentName } : {}),
  });
  const saved = unwrapApiResult(payload);

  if (editingId != null && Number(saved?.id) !== editingId) {
    try {
      await paletteService.deleteColor({ id: editingId });
    } catch (error) {
      if (import.meta.env.DEV) console.warn('[art-api] 编辑色卡后清理旧记录失败', error);
    }
  }

  return mapPaletteColorDtoToPlatform(saved, groupId);
}

export async function deletePaletteColorRemote(colorId) {
  await paletteService.deleteColor({ id: Number(colorId) || colorId });
}

export function resolvePaletteStoreKey(isGlobal, sessionStoreId) {
  if (isGlobal || isAllPlatformStore(sessionStoreId)) return GLOBAL_SHOP_KEY;
  return sessionStoreId;
}
